"use client"

import { useState } from "react"
import { ChevronDownIcon } from "@/components/ui/icons"
import { cn } from "@/lib/utils"

interface TocItem {
  id: string
  label: string
}

interface BiomarkerMobileTocProps {
  items: TocItem[]
  label: string
  className?: string
}

/**
 * Mobile-only collapsed disclosure for the article table of contents.
 * Tap to expand the anchor list; tapping a link smooth-scrolls and closes
 * the disclosure so the article content stays visible.
 */
export function BiomarkerMobileToc({
  items,
  label,
  className,
}: BiomarkerMobileTocProps) {
  const [open, setOpen] = useState(false)

  if (items.length === 0) return null

  const handleLinkClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    id: string
  ) => {
    e.preventDefault()
    const el = document.getElementById(id)
    if (!el) return
    el.scrollIntoView({ behavior: "smooth", block: "start" })
    window.history.replaceState(null, "", `#${id}`)
    setOpen(false)
  }

  return (
    <div className={cn("rounded-[16px] bg-white", className)}>
      <button
        type="button"
        onClick={() => setOpen((prev) => !prev)}
        aria-expanded={open}
        className="flex w-full items-center justify-between px-4 py-3 text-left"
      >
        <span className="type-label-lg text-neutral-400">{label}</span>
        <ChevronDownIcon
          className={cn(
            "text-neutral-400 transition-transform duration-200",
            open && "rotate-180"
          )}
          width={18}
          height={18}
        />
      </button>
      {open && (
        <ul className="flex flex-col gap-3 px-4 pb-4">
          {items.map((item) => (
            <li key={item.id}>
              <a
                href={`#${item.id}`}
                onClick={(e) => handleLinkClick(e, item.id)}
                className="type-body-md block text-neutral-600 transition-colors hover:text-neutral-900"
              >
                {item.label}
              </a>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
